import { message } from 'antd';
import { isAxiosError } from 'axios';
import { push, replace } from 'redux-first-history';
import { debounce, type Effect, type ForkEffect, put, select, takeEvery } from 'redux-saga/effects';
import * as Effects from 'redux-saga/effects';
import type { PayloadAction } from '@reduxjs/toolkit';

import { watchHandleError } from '~/redux/app/saga';
import { actions as app } from '~/redux/app/slice';
import { getBlockTarget } from '~/redux/app/selectors';

import type { Call, Contact, PointageStats, Tag } from '~/types/models';
import { type ErrorResponse, type IndexResponse, isErrorResponse, type PointageInitResponse } from '~/types/responses';

import * as Api from '~/api';

import { actions, type CallResponse, CallResponseColumns, type TakeCallPayload } from './slice';

const call: any = Effects.call;

export function* watchFetchCallTags({
  payload: groupIds,
}: PayloadAction<string[]>): Generator<Effect, void, IndexResponse<Tag>> {
  yield put(actions.setLoading(true));

  try {
    const groupIdsAndNull = [...groupIds, '¤'].join(',');
    const { items: tags } = yield call(
      Api.cache<Tag>,
      `call_tags:${groupIdsAndNull}`,
      async () =>
        await Api.index<Tag>('/tags', {
          group: groupIdsAndNull,
          sortBy: 'name',
          with: 'group',
          pageSize: 1000,
        }),
    );

    yield put(actions.setTags(tags));
    yield put(actions.setLoaded(true));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, []);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchFetchStats(): Generator<Effect, void, PointageStats> {
  try {
    const filters = yield select(state => state.pointage.statsFilters);
    const stats = yield call(Api.fetchPointageStats, filters);
    yield put(actions.setStats(stats));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, []);
    }
  }
}

export function* watchFetchPointageInit(): Generator<Effect, void, PointageInitResponse> {
  yield put(actions.setLoading(true));

  try {
    const { call: pointageCall, campaigns, groups }: PointageInitResponse = yield call(Api.fetchPointageInit);

    yield put(actions.setCall(pointageCall));
    yield put(actions.setCallCampaigns(campaigns));
    yield put(actions.setGroups(groups));
    yield put(actions.setLoaded(true));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, []);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchFreeCall({ payload: id }: PayloadAction<Call['id']>): Generator<Effect, void, any> {
  yield put(actions.setLoading(true));

  try {
    yield call(Api.freeCall, id);
    yield call(Api.clearCache, /^call_tags.*/);

    yield put(actions.setCall(null));
    yield put(actions.resetCallResponse());

    yield call(watchFetchPointageInit);

    yield put(app.blockState('proceeding'));
    const blockTarget = yield select<typeof getBlockTarget>(getBlockTarget);
    yield put(replace(blockTarget));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, []);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchNextCall({ payload: humanId }: PayloadAction<string>): Generator<Effect, void, Call> {
  yield put(actions.setLoading(true));

  try {
    const skippedContactIds = yield select((s) => s.pointage.skippedContactIds);
    let pointageCall: Call | null = null;

    if (humanId.startsWith('cam_')) {
      pointageCall = yield call(Api.getCampaignNextCall, humanId, skippedContactIds);
    } else if (humanId.startsWith('g_')) {
      pointageCall = yield call(Api.getGroupNextCall, humanId, skippedContactIds);
    }

    if (pointageCall) {
      yield put(actions.setCallReturn(false));
      yield put(actions.setCall(pointageCall));
      yield put(actions.resetCallResponse());

      if (humanId.startsWith('cam_')) {
        yield put(replace(`/campaigns/${humanId}/calls/${pointageCall.humanId}`));
      } else if (humanId.startsWith('g_')) {
        yield put(replace(`/groups/${humanId}/calls/${pointageCall.humanId}`));
      }
      yield put(actions.setCallLoaded(true));
    } else {
      message.success("Il n'y a plus d'appel disponible sur cette campagne.");

      yield put(replace('/'));
      yield put(actions.resetCallResponse());
      yield put(actions.reset());

      yield put(app.blockNavigation(false));
      yield call(watchFetchPointageInit);
    }
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      switch (e.response.data.status) {
        case 404:
          message.error("Prochain appel introuvable. Vous serez redirigé·e à l'accueil.", 10);
          yield put(replace('/'));
          break;
        default:
          yield call(watchHandleError, e.response.data, []);
      }
    }

    yield put(app.blockNavigation(false));
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchSaveCallResponse({
  payload: { contextId, id, response },
}: PayloadAction<{
  contextId: string;
  id: string;
  response: CallResponse;
}>): Generator<Effect, void, any> {
  yield put(actions.setLoading(true));

  try {
    yield call(Api.saveCallResponse, response, id);

    const currentCall = yield select(state => state.pointage.call);
    yield put(actions.addSkippedContactId(currentCall.contactId));

    yield put(actions.setCall(null));

    const isCallReturn = yield select(state => state.pointage.callReturn);
    if (isCallReturn) {
      yield put(actions.setCallReturn(false));
      yield put(actions.setCallLoaded(false));

      yield put(replace('/'));
      yield put(actions.resetCallResponse());
      yield put(actions.reset());

      yield put(app.blockNavigation(false));
      yield call(watchFetchPointageInit);
      return;
    }

    if (contextId.startsWith('cam_')) {
      yield put(replace(`/campaigns/${contextId}/calls/next`));
    } else if (contextId.startsWith('g_')) {
      yield put(replace(`/groups/${contextId}/calls/next`));
    }
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, CallResponseColumns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchSearch({
  payload,
}: PayloadAction<string>): Generator<Effect, void, IndexResponse<Contact>> {
  if (payload.length === 0) {
    yield put(actions.setSearchData([]));
    return;
  }

  yield put(actions.setSearchQuery(payload));
  yield put(actions.setSearchLoading(true));
  yield put(actions.setError(null));

  try {
    const { items, total } = yield call(Api.search, '/pointage', payload);

    yield put(actions.setSearchData(items));
    yield put(actions.setSearchTotal(total));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
    }
  } finally {
    yield put(actions.setSearchLoading(false));
  }
}

export function* watchSkipCall({ payload }: PayloadAction<Call>): Generator<Effect, void, any> {
  yield put(actions.setLoading(true));

  try {
    yield put(actions.addSkippedContactId(payload.contactId));
    yield call(Api.freeCall, payload.id);
    yield put(actions.setCall(null));

    if (payload.campaign) {
      yield put(replace(`/campaigns/${payload.campaign.humanId}/calls/next`));
    } else if (payload.group) {
      yield put(replace(`/groups/${payload.group.humanId}/calls/next`));
    }
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, CallResponseColumns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchTakeCall({ payload }: PayloadAction<TakeCallPayload>): Generator<Effect, void, any> {
  yield put(actions.setLoading(true));

  try {
    yield put(actions.setCall(null));
    yield put(actions.setCallLoaded(false));

    let pointageCall: Call;
    let path: string;
    switch (payload.type) {
      case 'CallCampaign':
        pointageCall = yield call(Api.takeCall, payload);

        if (!pointageCall?.campaign) {
          throw new Error("Erreur à la récupération de l'appel.");
        }

        path = `/campaigns/${pointageCall.campaign.humanId}/calls/${pointageCall.humanId}`;
        break;
      case 'Group':
        pointageCall = yield call(Api.takeCall, payload);

        if (!pointageCall?.group) {
          throw new Error("Erreur à la récupération de l'appel.");
        }

        path = `/groups/${pointageCall.group.humanId}/calls/${pointageCall.humanId}`;
        break;
    }

    yield put(push(path));
    yield put(actions.setCall(pointageCall));
    yield put(actions.setCall(pointageCall));
    yield put(actions.setCallLoaded(true));
    yield put(actions.setCallReturn(true));
  } catch (e) {
    yield call(Api.handleError, e);

    if (isAxiosError<ErrorResponse>(e) && isErrorResponse(e.response?.data) && e.response?.data) {
      yield call(actions.setError, e.response.data);
      yield call(watchHandleError, e.response.data, CallResponseColumns);
    }
  } finally {
    yield put(actions.setLoading(false));
  }
}

export function* watchPointageSagas(): Generator<ForkEffect, void> {
  yield takeEvery(actions.fetchCallTags, watchFetchCallTags);
  yield debounce(500, actions.fetchStats, watchFetchStats);
  yield debounce(500, actions.fetchPointageInit, watchFetchPointageInit);
  yield takeEvery(actions.freeCall, watchFreeCall);
  yield debounce(250, actions.nextCall, watchNextCall);
  yield takeEvery(actions.saveCallResponse, watchSaveCallResponse);
  yield takeEvery(actions.search, watchSearch);
  yield takeEvery(actions.skipCall, watchSkipCall);
  yield takeEvery(actions.takeCall, watchTakeCall);
}

const pointageSagas = watchPointageSagas;

export default pointageSagas;
